import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import styles from './ProductCard.module.scss';
import Checkbox from '../Checkbox';
import ProductImagesSlider from '../ProductImagesSlider';
import { ApprovedStatusLabel } from '../../../constants/approvedStatus';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getSupplierRetailersFromStateSelector } from '../../../utils/stateHelper';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';
import { ActiveStatus } from '../../../constants/activeStatus';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

function ProductCard(props) {
  const {
    product,
    approvedStatus,
    shouldAllowUserToDoActions,
    isVerboseInventoryInfo,
    slider = false,
    checked = false,
    checkbox = false,
  } = props;
  const card = useRef(null);

  const supplierRetailers = useSelector(getSupplierRetailersFromStateSelector);
  const { currentFilters } = useSelector((state) => state.productFilters);

  const isFilteringArchived = useMemo(() => {
    return currentFilters?.activeProducts === ActiveStatus.ARCHIVED;
  }, [currentFilters]);

  const shouldEnableSelection = shouldAllowUserToDoActions && !isFilteringArchived;

  const getModelAssignRetailers = (retailers) => {
    if (!retailers.length) {
      return <p>Not Approved</p>;
    } else if (retailers.length === 1) {
      return <p>Approved to 1 sale channel</p>;
    }

    return <p>Approved to {retailers.length} sales channels</p>;
  };

  const getOverlayTrigger = (assignedTo) => {
    const placement = 'top';
    if (!product.retailers.length) {
      return assignedTo ? getModelAssignRetailers(product.retailers) : null;
    }

    product.retailers = product.retailers.map((retailerName) =>
      supplierRetailerUtils.getRetailerDisplayNameFromRetailerName({
        supplierRetailers,
        retailerName,
      }),
    );

    return (
      <OverlayTrigger
        key={placement}
        placement={placement}
        overlay={
          <Tooltip id={`tooltip-${placement}`}>
            <h5>{product.retailers.join(', ')}</h5>
          </Tooltip>
        }
      >
        <div>{assignedTo ? getModelAssignRetailers(product.retailers) : null}</div>
      </OverlayTrigger>
    );
  };

  const getDesc = () => {
    const { assignedTo = true } = props;
    return (
      <>
        <p>
          {product.available_inventory} units{isVerboseInventoryInfo && ' In Cymbio'} - {product.variants.length}{' '}
          variants
        </p>
        {getOverlayTrigger(assignedTo)}
      </>
    );
  };

  const handleCheck = (e) => {
    e.preventDefault();
    const checkedPayload = {
      model: product.model,
      variants: product.variants,
    };
    props.onCheck(!checked, checkedPayload);
  };

  const openApproved = (e) => {
    if (approvedStatus && props.open) {
      props.open(product);
      e.stopPropagation();
    }
  };

  const showSlider = slider && product.square_images.length > 1;

  if (!product) {
    return null;
  }

  return (
    <div className={cn(styles.resultsProduct, approvedStatus ? styles.clickable : '')} ref={card}>
      <div className={styles.wrapper}>
        <div className={styles.wp}>
          <div className={styles.top}>
            {shouldEnableSelection ? (
              checkbox && (
                <div onClick={handleCheck}>
                  <Checkbox checked={checked} value={product.model} hideLabel />
                </div>
              )
            ) : (
              <div></div>
            )}

            {approvedStatus && (
              <button className={styles.approved_button} onClick={openApproved}>
                {ApprovedStatusLabel[approvedStatus]}
              </button>
            )}
          </div>
          <div onClick={openApproved}>
            {showSlider ? (
              <ProductImagesSlider images={product.square_images} cardRef={card} />
            ) : (
              <img alt="" src={product.square_images[0]} />
            )}

            <div className={showSlider ? styles.img_block : null}></div>

            <div className={cn(styles.desc, styles.withoutPrice)}>
              <div className={styles.pt}>
                <ResponsiveEllipsis text={product.title} maxLine="2" ellipsis="..." />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.details}>
          {getDesc()}
          <p>Model: {product.model}</p>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;

ProductCard.propTypes = {
  product: PropTypes.shape({
    images: PropTypes.array,
    variants: PropTypes.array,
    model: PropTypes.string,
  }).isRequired,
  checkbox: PropTypes.bool,
  onCheck: PropTypes.func.isRequired,
  checked: PropTypes.bool,
  approvedStatus: PropTypes.string,
  assignedTo: PropTypes.bool,
  slider: PropTypes.bool,
  open: PropTypes.func,
};
